/**
 * `timeline` — `TimelineValue` (`specs/M2-tier2-column-types.md` §5.3, `[09§A.7 timeline]`).
 *
 * A date **range**: `{"from":"YYYY-MM-DD","to":"YYYY-MM-DD","changed_at":…}`, no time of day
 * anywhere in the 311 populated cells of the 2026-09-10 snapshot. `text` is `"from - to"` with
 * spaces around the dash (`"2025-11-24 - 2025-11-30"`), and the fragment widens both ends to
 * midnight UTC instants (`"2025-11-24T00:00:00+00:00"`) `[cvs§2 timeline]`.
 *
 * Some cells also carry `textual_value` — an older, human-typed rendering monday kept beside
 * the dates (`{"to":"2020-06-05","from":"2020-05-24","changed_at":…,"textual_value":"2020-05-09
 * - 2020-05-21"}`). It is **not** what `text` renders (the sample above rendered `"2020-05-24 -
 * 2020-06-05"`), so it round-trips as a volatile extra and is never read.
 *
 * `visualization_type` is `"milestone"` on 1 of 549 sampled cells and null elsewhere; there is
 * **no documented JSON key to write it** `[09§A.7 timeline]`, so it is preserved on import and
 * never produced by a write (q334).
 */
import { z } from 'zod';
import { BLANK_COMPARE_VALUE, defaultFilterSql, defaultSummarySql, isBlankOnly, sqlFragment, } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function isRealDate(value) {
    if (!DATE_RE.test(value))
        return false;
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
import { timelineFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const timelineValueSchema = z.looseObject({
    from: z.string().refine(isRealDate, { message: 'expected YYYY-MM-DD' }),
    to: z.string().refine(isRealDate, { message: 'expected YYYY-MM-DD' }),
    visualization_type: z.string().nullable().optional(),
    changed_at: z.string().nullable().optional(),
    textual_value: z.string().optional(),
});
export const timelineSettingsSchema = z.looseObject({
    hide_footer: z.boolean().optional(),
    show_set_as_milestone: z.boolean().optional(),
    show_weekends: z.boolean().optional(),
    show_week_number: z.boolean().optional(),
});
/** Compare values monday's own timeline filter offers `[09§A.7 timeline]`. */
export const TIMELINE_FILTER_VALUES = [
    'CURRENT',
    'DUE_TODAY',
    'FUTURE_TIMELINE',
    'PAST_TIMELINE',
    'DONE_ON_TIME',
    'OVERDUE',
    'DONE_OVERDUE',
    'MILESTONE',
    BLANK_COMPARE_VALUE,
];
function readDate(record, key) {
    const raw = record[key];
    if (raw === null || raw === undefined || raw === '')
        return null;
    if (typeof raw !== 'string')
        throw new ColumnValueError('timeline', `"${key}" must be a string`);
    // The fragment widens both ends to an instant; accept it back so a re-import round-trips.
    const date = raw.slice(0, 10);
    if (!isRealDate(date))
        throw new ColumnValueError('timeline', `"${raw}" is not a valid date`);
    return date;
}
function normalize(raw, keepVolatile) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw !== 'object') {
        throw new ColumnValueError('timeline', `expected an object, received ${typeof raw}`);
    }
    const record = raw;
    // `{}` is a documented clear form for this type `[09§A.7 rule 4]`.
    const from = readDate(record, 'from');
    const to = readDate(record, 'to');
    if (from === null && to === null)
        return null;
    if (from === null || to === null) {
        throw new ColumnValueError('timeline', 'a timeline needs both "from" and "to"');
    }
    if (from > to)
        throw new ColumnValueError('timeline', '"from" is after "to"');
    const value = { from, to };
    if (keepVolatile) {
        if (record.visualization_type !== undefined) {
            value.visualization_type = (record.visualization_type ?? null);
        }
        if (typeof record.changed_at === 'string')
            value.changed_at = record.changed_at;
        if (typeof record.textual_value === 'string')
            value.textual_value = record.textual_value;
    }
    return value;
}
/** `"2025-11-24 - 2025-11-30"` — measured on all 311 populated cells. */
function renderText(value) {
    return value === null ? '' : `${value.from} - ${value.to}`;
}
/** One end of the range as the shadow keys a filter compares (`compare_attribute`). */
function edgeKeys(day) {
    const date = new Date(`${day}T00:00:00Z`);
    return { text: day, number: date.getTime(), date };
}
/** Whole days covered, both ends inclusive — the editor's "36 days selected" (capture). */
export function timelineDays(value) {
    const from = Date.parse(`${value.from}T00:00:00Z`);
    const to = Date.parse(`${value.to}T00:00:00Z`);
    return Math.round((to - from) / 86_400_000) + 1;
}
export const timelineColumn = defineColumnType({
    type: 'timeline',
    title: 'Timeline',
    description: 'A start and an end date.',
    apiFragmentName: 'TimelineValue',
    legacyTypeName: 'timerange',
    idPrefix: 'timeline',
    valueSchema: timelineValueSchema,
    settingsSchema: timelineSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: timelineSettingsSchema,
    writable: true,
    /** *"The `change_simple_column_value` mutation is not supported"* `[09§A.7 timeline]`. */
    supportsSimpleValue: false,
    /** `timeline` also accepts `{}` as a clear `[09§A.7 rule 4]`. */
    clearForms: ['null', 'empty-object'],
    filterConditions: timelineFilterConditions,
    filterOperators: operatorsOf(timelineFilterConditions),
    aggregations: ['earliest', 'latest', 'range', 'count', 'count_empty', 'none'],
    emptyText: '',
    parseValue(raw) {
        return normalize(raw, false);
    },
    toText(value) {
        return renderText(value);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        const start = new Date(`${value.from}T00:00:00Z`);
        // Sorting a range sorts by its **start**, like every Gantt-shaped view does.
        return { text: textShadow(renderText(value)), number: start.getTime(), date: start };
    },
    isEqual(a, b) {
        if (a === null || a === undefined)
            return b === null || b === undefined;
        if (b === null || b === undefined)
            return false;
        return (a.from === b.from &&
            a.to === b.to &&
            (a.visualization_type ?? null) === (b.visualization_type ?? null));
    },
    toActivityValue(value) {
        return value === null ? null : { from: value.from, to: value.to };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { from: value.from, to: value.to };
        if (value.visualization_type !== undefined)
            out.visualization_type = value.visualization_type;
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        if (value.textual_value !== undefined)
            out.textual_value = value.textual_value;
        return out;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return timelineSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        // Both ends widen to midnight UTC in the fragment `[cvs§2 timeline]`.
        return {
            from: `${value.from}T00:00:00+00:00`,
            to: `${value.to}T00:00:00+00:00`,
            visualization_type: value.visualization_type ?? null,
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    /** Facet by the rendered range: a range is one bucket, not two dates. */
    bucketsOf(value) {
        return value === null ? [] : [{ key: `${value.from}..${value.to}`, label: renderText(value) }];
    },
    /**
     * The two halves monday's dropdown compares (`compare_attribute`, six rows each).
     *
     * `start` **is** the date shadow (`toSortKeys` sorts a range by its start), so it needs no
     * override; `end` lives only inside the stored JSON, and `(cv.value->>'to')::date` is
     * what the operator table compares for it. Before round 5z both halves were evaluated
     * against the shadow, so *ends after X* answered *starts after X* and a timeline filter
     * dropped items it should have kept (`tasks/T-M4-V2-V4-V7.md`, round 5x).
     */
    compareAttributes: {
        start: {
            shadow: 'date',
            keys(value) {
                return value === null
                    ? { text: null, number: null, date: null }
                    : edgeKeys(value.from);
            },
        },
        end: {
            shadow: 'date',
            sql: (cell) => `(${cell}.value->>'to')::date`,
            keys(value) {
                return value === null ? { text: null, number: null, date: null } : edgeKeys(value.to);
            },
        },
    },
    /**
     * The date shadow holds the **start**, so the ordinary date SQL answers every operator the
     * builder offers. monday's named compare values (`OVERDUE`, `MILESTONE`, …) are a second
     * vocabulary that needs the *other* end and the item's status; they are not implemented and
     * are recorded as q335 — an unsupported compare value filters nothing rather than
     * filtering wrongly.
     */
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        if (ctx.operator === 'is_empty' || (ctx.operator === 'any_of' && isBlankOnly(ctx.compareValues))) {
            return sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.date_value IS NULL)`);
        }
        if (ctx.operator === 'is_not_empty')
            return sqlFragment(`${cell}.date_value IS NOT NULL`);
        const named = ctx.compareValues.some((entry) => typeof entry === 'string' && TIMELINE_FILTER_VALUES.includes(entry));
        // A named compare value needs the *other* end and the item's status: not implemented (q335).
        if (named)
            return null;
        return defaultFilterSql('date', ctx);
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map